import React from 'react';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

const sweep = keyframes`
  0% { transform: translateX(-120%) skewX(-12deg); opacity: 0; }
  35% { opacity: 0.35; }
  50% { transform: translateX(0%) skewX(-12deg); opacity: 0.15; }
  65% { opacity: 0.35; }
  100% { transform: translateX(120%) skewX(-12deg); opacity: 0; }
`;

export default function SignUpVisual({
    src = '/assets/images/signup.jpg',
    intensity = 'medium', // 'soft' | 'medium' | 'strong'
    animated = true,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const tintA = alpha(theme.palette.primary.main, isDark ? 0.28 : 0.14);
    const tintB = alpha(theme.palette.secondary.main, isDark ? 0.20 : 0.10);
    const vignette = alpha(isDark ? theme.palette.common.black : theme.palette.primary.dark, isDark ? 0.38 : 0.22);
    const ringStroke = alpha(isDark ? theme.palette.text.secondary : '#fff', isDark ? 0.22 : 0.14);
    const meshA = alpha(theme.palette.primary.main, isDark ? 0.36 : 0.22);
    const meshB = alpha(theme.palette.secondary.main, isDark ? 0.28 : 0.18);
    const meshC = alpha(theme.palette.primary.dark, isDark ? 0.24 : 0.14);

    const scale =
        intensity === 'soft' ? 0.7 :
            intensity === 'strong' ? 1.2 :
                1.0;

    return (
        <Box
            sx={{
                position: 'relative',
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                // Base image
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${src})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    transform: 'scale(1.02)',
                    filter: isDark ? 'saturate(110%) contrast(105%)' : 'saturate(105%) contrast(102%)',
                },
            }}
        >
            {/* Brand tint */}
            <Box
                className="overlay overlay--tint"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    mixBlendMode: 'multiply',
                    background: `linear-gradient(135deg, ${tintA} 0%, ${tintB} 100%)`,
                    opacity: 1 * scale,
                }}
            />

            {/* Mesh glow spots (subtle brand blobs) */}
            <Box
                className="overlay overlay--mesh"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    mixBlendMode: isDark ? 'screen' : 'overlay',
                    backgroundImage: `
            radial-gradient(550px 360px at 85% 20%, ${meshA} 0%, transparent 70%),
            radial-gradient(420px 300px at 20% 85%, ${meshB} 0%, transparent 70%),
            radial-gradient(380px 260px at 65% 60%, ${meshC} 0%, transparent 70%)
          `,
                    opacity: isDark ? 0.55 * scale : 0.35 * scale,
                    filter: 'blur(0.2px)',
                }}
            />

            {/* Concentric rings (very subtle topographic lines) */}
            <Box
                className="overlay overlay--rings"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    backgroundImage: `
            radial-gradient(circle at 70% 50%, ${ringStroke} 0px, transparent 1px),
            radial-gradient(circle at 70% 50%, transparent 0, transparent 60px, ${ringStroke} 60px, transparent 61px),
            radial-gradient(circle at 70% 50%, transparent 0, transparent 120px, ${ringStroke} 120px, transparent 121px),
            radial-gradient(circle at 70% 50%, transparent 0, transparent 180px, ${ringStroke} 180px, transparent 181px),
            radial-gradient(circle at 70% 50%, transparent 0, transparent 240px, ${ringStroke} 240px, transparent 241px),
            radial-gradient(circle at 70% 50%, transparent 0, transparent 300px, ${ringStroke} 300px, transparent 301px)
          `,
                    backgroundBlendMode: 'soft-light',
                    opacity: 0.35 * scale,
                }}
            />

            {/* Vignette to keep edges calm and text-friendly if needed */}
            <Box
                className="overlay overlay--vignette"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background: `radial-gradient(120% 120% at 60% 50%, transparent 45%, ${vignette} 100%)`,
                    opacity: 0.9 - 0.4 * (scale - 1), // subtle change with intensity
                }}
            />

            {/* Animated sweep highlight (disabled for prefers-reduced-motion) */}
            <Box
                className="overlay overlay--sweep"
                sx={{
                    position: 'absolute',
                    top: '-20%',
                    bottom: '-20%',
                    left: '-25%',
                    right: '-25%',
                    pointerEvents: 'none',
                    background: `linear-gradient(90deg, transparent 0%, ${alpha('#fff', isDark ? 0.08 : 0.12)} 50%, transparent 100%)`,
                    mixBlendMode: isDark ? 'screen' : 'overlay',
                    transform: 'translateX(-120%) skewX(-12deg)',
                    animation: animated ? `${sweep} ${isDark ? 9 : 11}s ease-in-out infinite` : 'none',
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                    },
                    opacity: 0.4 * scale,
                }}
            />

            {/* Grain texture (tiny SVG data URI) */}
            <Box
                className="overlay overlay--grain"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    opacity: isDark ? 0.07 : 0.05,
                    mixBlendMode: isDark ? 'soft-light' : 'multiply',
                    backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(`
            <svg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'>
              <filter id='n'>
                <feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>
                <feColorMatrix type='saturate' values='0'/>
              </filter>
              <rect width='100%' height='100%' filter='url(#n)' opacity='0.55'/>
            </svg>
          `)}")`,
                }}
            />
        </Box>
    );
}